import { useMemo } from 'react';
import { ADMIN_NAVIGATION_ITEMS } from '@/constants/admin/adminNavigation';
import { INavigationItem, IUserState } from '@/types/admin/navigation';
import { useAdminUser } from './useAdminUser';

/**
 * Hook to get filtered navigation items based on current admin user permissions
 * Matches the logic from admin-web's MainNav component
 */
export const useAdminNavigation = () => {
  // Get admin user data from auth state (transformed)
  const adminUser = useAdminUser();
  
  // Filter navigation items based on user conditions (matching web logic)
  const filteredNavigationItems = useMemo(() => {
    
    const filtered = ADMIN_NAVIGATION_ITEMS.filter((item: INavigationItem) => {
      if (item.condition && typeof item.condition === 'function') {
        const hasAccess = item.condition(adminUser);
        return hasAccess;
      }
      return true; // Show item if no condition
    });
    
    return filtered;
  }, [adminUser]);

  // Check if a path is currently active (matching web logic)
  const isPathActive = useMemo(() => {
    return (itemPath: string, subNavItems?: any[], currentPath?: string) => {
      if (!currentPath) return false;
      
      const sanitizedCurrentPath = currentPath.split('/')[1];
      const availablePathNames = [itemPath, ...(subNavItems?.map((item) => item.path) || [])];
      return availablePathNames.some(
        (p) => currentPath.endsWith(p) || p === `/${sanitizedCurrentPath}`
      );
    };
  }, []);

  // Filter sub navigation items based on user conditions (matching web logic)
  const getFilteredSubNavItems = useMemo(() => {
    return (subNavItems?: any[]) => {
      if (!subNavItems) return undefined;
      
      return subNavItems.filter(subItem => {
        if (subItem.condition && typeof subItem.condition === 'function') {
          return subItem.condition(adminUser);
        }
        return true;
      });
    };
  }, [adminUser]);

  return {
    navigationItems: filteredNavigationItems,
    adminUser,
    isPathActive,
    getFilteredSubNavItems,
  };
};
